import api, { endpoints } from './base'
import type {
  PermissionGroup,
  PermissionGroupCreate,
  PermissionGroupUpdate,
  UserPermissionGroup,
} from './types/permission-groups'

/**
 * API сервис для управления группами разрешений
 */

/**
 * Получить список всех групп разрешений
 */
export const getPermissionGroups = async (params?: {
  skip?: number
  limit?: number
  active_only?: boolean
}): Promise<PermissionGroup[]> => {
  try {
    const response = await api.get(endpoints.permissionGroups.list(), { params })
    return response.data
  } catch (error: any) {
    console.error('API Error getPermissionGroups:', error.response || error)
    throw error
  }
}

/**
 * Создать новую группу разрешений
 */
export const createPermissionGroup = async (data: PermissionGroupCreate): Promise<PermissionGroup> => {
  try {
    const response = await api.post(endpoints.permissionGroups.create(), data)
    return response.data
  } catch (error: any) {
    console.error('API Error createPermissionGroup:', error.response || error)
    throw error
  }
}

/**
 * Получить группу разрешений по ID
 */
export const getPermissionGroup = async (groupId: number): Promise<PermissionGroup> => {
  try {
    const response = await api.get(endpoints.permissionGroups.one(groupId))
    return response.data
  } catch (error: any) {
    console.error('API Error getPermissionGroup:', error.response || error)
    throw error
  }
}

/**
 * Обновить группу разрешений
 */
export const updatePermissionGroup = async (groupId: number, data: PermissionGroupUpdate): Promise<PermissionGroup> => {
  try {
    const response = await api.put(endpoints.permissionGroups.update(groupId), data)
    return response.data
  } catch (error: any) {
    console.error('API Error updatePermissionGroup:', error.response || error)
    throw error
  }
}

/**
 * Удалить группу разрешений (мягкое удаление)
 */
export const deletePermissionGroup = async (groupId: number): Promise<void> => {
  try {
    await api.delete(endpoints.permissionGroups.delete(groupId))
  } catch (error: any) {
    console.error('API Error deletePermissionGroup:', error.response || error)
    throw error
  }
}

/**
 * Назначить группу разрешений пользователю
 */
export const assignGroupToUser = async (userId: number, groupId: number): Promise<UserPermissionGroup> => {
  try {
    const response = await api.post(endpoints.permissionGroups.assignToUser(userId, groupId))
    return response.data
  } catch (error: any) {
    console.error('API Error assignGroupToUser:', error.response || error)
    throw error
  }
}

/**
 * Удалить группу разрешений у пользователя
 */
export const removeGroupFromUser = async (userId: number, groupId: number): Promise<void> => {
  try {
    await api.delete(endpoints.permissionGroups.removeFromUser(userId, groupId))
  } catch (error: any) {
    console.error('API Error removeGroupFromUser:', error.response || error)
    throw error
  }
}

/**
 * Получить все группы разрешений пользователя
 */
export const getUserPermissionGroups = async (userId: number): Promise<PermissionGroup[]> => {
  try {
    // console.log('Loading user permission groups for userId:', userId)
    const url = endpoints.permissionGroups.getUserGroups(userId)
    // console.log('Request URL:', url)

    const response = await api.get(url)
    // console.log('User permission groups response:', response.data)
    return response.data
  } catch (error: any) {
    console.error('API Error getUserPermissionGroups:', error.response || error)
    if (error.response) {
      console.error('Response data:', error.response.data)
      console.error('Response status:', error.response.status)
    }
    throw error
  }
}

/**
 * Получить всех пользователей группы разрешений
 */
export const getGroupUsers = async (groupId: number): Promise<any[]> => {
  try {
    const response = await api.get(endpoints.permissionGroups.getGroupUsers(groupId))
    return response.data
  } catch (error: any) {
    console.error('API Error getGroupUsers:', error.response || error)
    throw error
  }
}

/**
 * Массовое назначение групп разрешений пользователю в проекте
 */
export const assignMultipleGroupsToUser = async (
  userId: number,
  groupIds: number[],
  projectId?: number,
): Promise<void> => {
  try {
    const requestData = {
      group_ids: groupIds,
      project_id: projectId || null,
    }

    console.log('Sending request data:', requestData)
    console.log('Request URL:', endpoints.permissionGroups.assignMultipleToUser(userId))

    const response = await api.post(endpoints.permissionGroups.assignMultipleToUser(userId), requestData)
    return response.data
  } catch (error: any) {
    console.error('API Error assignMultipleGroupsToUser:', error.response || error)
    if (error.response) {
      console.error('Response data:', error.response.data)
      console.error('Response status:', error.response.status)

      // Детали ошибки валидации
      if (error.response.data && error.response.data.detail) {
        console.error('Validation error details:', error.response.data.detail)
        if (Array.isArray(error.response.data.detail)) {
          error.response.data.detail.forEach((detail: any, index: number) => {
            console.error(`Validation error ${index + 1}:`, detail)
          })
        }
      }
    }
    throw error
  }
}

/**
 * Назначение пользователя в проекты
 */
export const assignUserToProjects = async (userId: number, projectIds: number[]): Promise<void> => {
  try {
    console.log('Assigning user to projects:', userId, projectIds)
    console.log('Request URL:', endpoints.permissionGroups.assignUserToProjects(userId))
    console.log('Request data:', projectIds)
    const response = await api.post(endpoints.permissionGroups.assignUserToProjects(userId), projectIds)
    return response.data
  } catch (error: any) {
    console.error('API Error assignUserToProjects:', error.response || error)
    throw error
  }
}

/**
 * Получить проекты пользователя
 */
export const getUserProjects = async (userId: number): Promise<any[]> => {
  try {
    console.log('Loading user projects for userId:', userId)
    const response = await api.get(`/permission-groups/users/${userId}/projects/`)
    console.log('User projects response:', response.data)
    return response.data
  } catch (error: any) {
    console.error('API Error getUserProjects:', error.response || error)
    throw error
  }
}

/**
 * Удалить пользователя из проектов
 */
export const removeUserFromProjects = async (userId: number, projectIds: number[]): Promise<void> => {
  try {
    console.log('Removing user from projects:', userId, projectIds)
    const response = await api.delete(`/permission-groups/users/${userId}/projects/`, {
      data: projectIds,
    })
    console.log('Remove user from projects response:', response.data)
    return response.data
  } catch (error: any) {
    console.error('API Error removeUserFromProjects:', error.response || error)
    throw error
  }
}

/**
 * Получить группы разрешений пользователя в проекте
 */
export const getUserGroupsInProject = async (userId: number, projectId: number): Promise<PermissionGroup[]> => {
  try {
    // console.log('Loading user groups in project:', userId, projectId)
    const response = await api.get(`/permission-groups/users/${userId}/projects/${projectId}/groups/`)
    // console.log('User groups in project response:', response.data)
    return response.data
  } catch (error: any) {
    console.error('API Error getUserGroupsInProject:', error.response || error)
    throw error
  }
}

export type UserProjectPermissionGroups = {
  project_id: number
  project_name?: string | null
  groups: PermissionGroup[]
}

/**
 * Batch: all project-scoped permission groups for a user (avoids N+1 on login).
 */
export const getUserGroupsByProject = async (userId: number): Promise<UserProjectPermissionGroups[]> => {
  try {
    const response = await api.get(endpoints.permissionGroups.getUserGroupsByProject(userId))
    return Array.isArray(response.data) ? response.data : []
  } catch (error: any) {
    console.error('API Error getUserGroupsByProject:', error.response || error)
    throw error
  }
}

/**
 * Удалить все группы пользователя из проекта
 */
export const removeUserGroupsFromProject = async (userId: number, projectId: number): Promise<void> => {
  try {
    console.log('Removing user groups from project:', userId, projectId)
    const response = await api.delete(`/permission-groups/users/${userId}/projects/${projectId}/groups/`)
    console.log('Remove user groups from project response:', response.data)
    return response.data
  } catch (error: any) {
    console.error('API Error removeUserGroupsFromProject:', error.response || error)
    throw error
  }
}

/**
 * Проверка здоровья системы групп разрешений
 */
export const getPermissionGroupsHealth = async (): Promise<any> => {
  try {
    const response = await api.get(endpoints.permissionGroups.health())
    return response.data
  } catch (error: any) {
    console.error('API Error getPermissionGroupsHealth:', error.response || error)
    throw error
  }
}
